import { BaseApi, getBaseApi } from "../BaseApi";
import { buildUrl } from "../utils";
import { downloadOrderImportTemplate } from "../../orderImportTemplate";

// Idempotent list/summary GETs go through the 30s in-memory cache so
// navigating away and back doesn't refetch; mutations below bust the cache.
function getOrders(params: {
  limit: number;
  skip: number;
  keyword?: string;
  owner?: string;
  country?: string;
  status?: string;
  agent?: string;
  product?: string;
  boutique?: string;
}) {
  const baseUrl = "orders";
  const url = buildUrl(baseUrl, params);
  return getBaseApi().getCached(url);
}

function getOrdersSummary(params?: {
  owner?: string;
  country?: string;
  product?: string;
  boutique?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const baseUrl = "orders/summary";
  const url = buildUrl(baseUrl, params || {});
  return getBaseApi().getCached(url);
}

// Wrap a mutation response so any cached orders list is dropped on success —
// the next list load then reflects the mutation instead of returning stale
// cached data. Used across create/update/delete on this module.
const withOrderInvalidate = <T,>(p: Promise<T>): Promise<T> =>
  p.then((res) => {
    BaseApi.invalidateCache("orders");
    return res;
  });

function createOrderApi(params: {
  country: string;
  product: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  price: number;
  quantity: number;
  sourceLink: string;
  sourceName: string;
  totalPrice: number;
}) {
  const baseUrl = "orders";
  const url = buildUrl(baseUrl);
  return withOrderInvalidate(getBaseApi().post(url, params));
}

function deleteOrderApi(params: { ids: string[] }) {
  const baseUrl = "orders";
  const url = `${baseUrl}/delete`;
  return withOrderInvalidate(getBaseApi().put(url, params));
}

function updateOrderApi(params: {
  id: string;
  product: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  price: number;
  quantity: number;
  sourceLink: string;
  sourceName: string;
  totalPrice: number;
}) {
  const baseUrl = "orders";
  const url = `${baseUrl}/${params.id}`;
  return withOrderInvalidate(getBaseApi().put(url, params));
}

function updateStatusOrderApi(params: {
  id: string;
  status: string;
  comment: string;
  delivery?: string;
  quantity?: number;
  /** Montant total de la commande à la confirmation (prix unitaire recalculé côté serveur) */
  totalPrice?: number;
  /** Lignes produit affilié (pays = commande), stock décrémenté à la confirmation */
  affiliateAddOns?: { affiliateId: string; quantity: number; unitPrice?: number }[];
  /** Structured call outcome — required on notReachable / callback (see CallDisposition enum on backend) */
  disposition?: string;
  /** ISO datetime for scheduled callback — required on status=callback */
  callbackAt?: string;
  /** ISO datetime — date de livraison souhaitée (optionnelle, uniquement sur confirmation) */
  scheduledFor?: string;
}) {
  const baseUrl = "orders";
  const url = `${baseUrl}/status/${params.id}`;
  return withOrderInvalidate(getBaseApi().put(url, params));
}

function getOrder(params: { id: string }) {
  const baseUrl = "orders";
  const url = `${baseUrl}/${params.id}`;
  try {
    return getBaseApi().get(url);
  } catch (error) {
    console.error("Error fetching Order:", error);
    throw error;
  }
}

function downloadSampleOrderApi(_params: {} = {}) {
  downloadOrderImportTemplate();
  return Promise.resolve();
}

function uploadOrdersApi(params: { file: any }) {
  const baseUrl = "orders";
  const url = `${baseUrl}/upload-multiple`;
  return withOrderInvalidate(getBaseApi().post(url, params.file));
}

function bulkUpdateAgentApi(params: { orderIds: string[]; agentId: string }) {
  const baseUrl = "orders";
  const url = `${baseUrl}/bulk-update-agent`;
  return withOrderInvalidate(getBaseApi().put(url, params));
}

function getOrderStatsApi(params: {
  startDate: string;
  endDate: string;
  owner?: string;
  boutique?: string;
  country?: string;
}) {
  const baseUrl = "orders/order-stats";
  const clean: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v) clean[k] = String(v);
  });
  const url = buildUrl(baseUrl, clean as any);
  try {
    return getBaseApi().get(url);
  } catch (error) {
    console.error("Error fetching order stats:", error);
    throw error;
  }
}

function getOrderAgentDetailApi(params: {
  id: string;
  startDate: string;
  endDate: string;
  owner?: string;
  boutique?: string;
  country?: string;
}) {
  const { id, ...rest } = params;
  const clean: Record<string, string> = {};
  Object.entries(rest).forEach(([k, v]) => {
    if (v) clean[k] = String(v);
  });
  const url = buildUrl(`orders/agent-detail/${encodeURIComponent(id)}`, clean as any);
  return getBaseApi().get(url);
}

function getOrderAgentStatsApi(params: {
  startDate: string;
  endDate: string;
  owner?: string;
  boutique?: string;
  country?: string;
}) {
  const baseUrl = "orders/agent-stats";
  const clean: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v) clean[k] = String(v);
  });
  const url = buildUrl(baseUrl, clean as any);
  try {
    return getBaseApi().get(url);
  } catch (error) {
    console.error("Error fetching order agent stats:", error);
    throw error;
  }
}

function getOrderTransitions(id: string) {
  try {
    return getBaseApi().get(`orders/${id}/transitions`);
  } catch (error) {
    console.error("Error fetching order transitions:", error);
    throw error;
  }
}

function bulkImportOrdersApi(payload: { rows: any[]; dryRun?: boolean }) {
  return withOrderInvalidate(getBaseApi().post("orders/bulk-import", payload));
}

function parseOrderImportFileApi(file: File) {
  const form = new FormData();
  form.append("file", file);
  return getBaseApi().axiosInstance.post("orders/parse-import-file", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

function parseGoogleSheetOrdersApi(url: string) {
  return getBaseApi().post("orders/parse-google-sheet", { url });
}

function getCustomerHistoryApi(phone: string) {
  return getBaseApi().get(`orders/customer-history/${encodeURIComponent(phone)}`);
}

// Fetches only the history track for a single order — used by the HistoryModal
// on demand so we don't have to include heavy history payloads on every list
// row. Server strips staff-only fields for sellers automatically.
function getOrderHistoryApi(orderId: string) {
  return getBaseApi().get(`orders/${encodeURIComponent(orderId)}/history`);
}

function getMyQueueApi() {
  return getBaseApi().get("orders/my-queue");
}

function getAgentsPerformanceApi(params: {
  from?: string;
  to?: string;
  country?: string;
}) {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.country) qs.set("country", params.country);
  const q = qs.toString();
  return getBaseApi().get(`agents/performance${q ? `?${q}` : ""}`);
}

export {
  getOrders,
  getOrdersSummary,
  createOrderApi,
  getOrder,
  updateOrderApi,
  deleteOrderApi,
  updateStatusOrderApi,
  downloadSampleOrderApi,
  uploadOrdersApi,
  bulkUpdateAgentApi,
  getOrderStatsApi,
  getOrderAgentStatsApi,
  getOrderAgentDetailApi,
  getOrderTransitions,
  bulkImportOrdersApi,
  parseOrderImportFileApi,
  parseGoogleSheetOrdersApi,
  getCustomerHistoryApi,
  getOrderHistoryApi,
  getMyQueueApi,
  getAgentsPerformanceApi,
};
